import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sanityClient from "../sanityClient";
import tempImage from "../assets/images/temp.jpg";
import { Link } from "react-router-dom";

const LatestStories = () => {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "post"] | order(publishedAt desc){
          _id,
          title,
          "author": author->name,
          "authorImage": author->image.asset->url,
          publishedAt,
          body,
          "imageUrl": mainImage.asset->url
        }`
      )
      .then((data) => {
        const currentDate = new Date();

        // Filter out posts that have a future publish date
        const filteredData = data.filter((item) => {
          const postDate = item.publishedAt ? new Date(item.publishedAt) : null;
          return postDate && postDate <= currentDate; // Ensure it's a valid past/present date
        });

        // Take only the 3 most recent posts
        const latestPosts = filteredData.slice(0, 3);

        const formattedData = latestPosts.map((item) => ({
          id: item._id,
          title: item.title,
          date: formatDate(item.publishedAt),
          author: item.author,
          authorImage: item.authorImage || tempImage,
          excerpt: formatExcerpt(item.body),
          image: item.imageUrl || tempImage,
        }));

        setStories(formattedData);
      })
      .catch(console.error);
  }, []);

  const formatExcerpt = (body) => {
    const block = body.find((block) => block._type === "block");
    return block && block.children
      ? block.children
          .map((child) => child.text)
          .join("")
          .split(" ")
          .slice(0, 20)
          .join(" ") + "..."
      : "Read more about this story...";
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const date = new Date(dateString);
    return `Published on ${date.toLocaleDateString("en-US", options)}`;
  };

  return (
    <Wrapper>
      <Container>
        <Title>Latest Stories</Title>
        <StoryGrid>
          {stories.map((story) => (
            <Link to={`/blog/${story.id}`} key={story.id}>
              <StoryCard>
                <StoryImage src={story.image} alt={story.title} />
                <StoryContent>
                  <StoryTitle>{story.title}</StoryTitle>
                  <AuthorContainer>
                    <AuthorImage src={story.authorImage} alt={story.author} />
                    <StoryMeta>
                      <StoryDate>{story.date}</StoryDate>
                      <StoryAuthor>by {story.author}</StoryAuthor>
                    </StoryMeta>
                  </AuthorContainer>
                  <StoryExcerpt>{story.excerpt}</StoryExcerpt>
                  <ReadMoreLink>Read more</ReadMoreLink>
                </StoryContent>
              </StoryCard>
            </Link>
          ))}
        </StoryGrid>
        <AllStoriesLink to="/blog">Read All Stories</AllStoriesLink>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  background-color: var(--hhb-primary);
  padding: 20px 0;
`;

const Container = styled.div`
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  color: var(--hhb-white);
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  color: var(--hhb-white);
  margin-bottom: 20px;
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const StoryCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  text-decoration: none;
  color: inherit;
  height: 100%;
`;

const StoryImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const StoryContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const StoryTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  text-decoration: none;
  margin-bottom: 10px;
`;

const AuthorContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  text-align: center;
`;

const AuthorImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

const StoryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  color: #333;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const StoryDate = styled.span`
  color: #6c757d;
`;

const StoryAuthor = styled.span``;

const StoryExcerpt = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 10px;
`;

const ReadMoreLink = styled.span`
  display: block;
  color: var(--hhb-primary);
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    text-decoration: none;
    color: var(--hhb-darkblue);
  }
`;

const AllStoriesLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 20px;
  font-size: 1.2rem;
  color: var(--hhb-white);
  text-decoration: underline;

  &:hover {
    color: var(--hhb-yellow);
    text-decoration: none;
  }
`;

export default LatestStories;
